
import {
  Box,
  Text,
  Flex,
  Badge,
  Button,
  useColorModeValue,
  VStack,
  IconButton,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Icon,
  Tooltip,
  SimpleGrid,
  Image,
  Spinner
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdEdit, MdFileDownload } from 'react-icons/md';
import { CloseIcon } from '@chakra-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Vendor, vendorRepository } from 'api/vendorRepository';
import { useAuth } from "contexts/authContext";
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, useDisclosure, } from '@chakra-ui/react';
import EditVendorForm from '../EditVendor';
import { Alert, AlertIcon, AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter, Center } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import UpdateProductModal from '../EditProduct';
import { Avatar } from "@chakra-ui/react";

interface VendorDetailPanelsProps {
  vendor: Vendor | null;
  setVendor: (vendor: Vendor) => void;
  fetchVendorProducts: () => void;
  onProductUpdated: () => void;
}

type DeleteProductPayload = {
  vendorProductId: string;
  productId: string;
};

export default function VendorDetailPanels({ vendor, setVendor, fetchVendorProducts, onProductUpdated }: VendorDetailPanelsProps) {
  const cardBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('navy.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const infoBg = useColorModeValue('gray.50', 'navy.700');
  const { role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertStatus, setCenterAlertStatus] = useState<"success" | "error" | "warning">("success");
  const [centerAlertMessage, setCenterAlertMessage] = useState("");
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [vendorProducts, setVendorProducts] = useState({ vendorProductId: "", vendorId: "", vendorName: "", products: [], });
  const [selectedVendorProductId, setSelectedVendorProductId] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [deleteProductPayload, setDeleteProductPayload] = useState<DeleteProductPayload | null>(null);
  const [filePreviews, setFilePreviews] = useState<Record<string, string>>({});
  const [loadingFiles, setLoadingFiles] = useState<Record<string, boolean>>({});

  const allAttachments = useMemo(() => {
    return Array.isArray(vendor?.attachments) ? vendor.attachments.filter((a): a is string => typeof a === 'string') : [];
  }, [vendor]);

  useEffect(() => {
    if (allAttachments.length === 0) return;

    allAttachments.forEach((fileKey: string) => {
      loadFilePreview(fileKey);
    });
  }, [allAttachments]);

  const loadFilePreview = async (fileKey: string) => {
    if (filePreviews[fileKey]) return;

    try {
      setLoadingFiles((prev) => ({ ...prev, [fileKey]: true }));

      const blob = await vendorRepository.downloadVendorFile(fileKey);
      const blobUrl = URL.createObjectURL(blob);

      const lower = fileKey.toLowerCase();
      const isImage = lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png");

      if (isImage) {
        const img = new window.Image();
        img.src = blobUrl;
        img.onload = () => {
          setFilePreviews((prev) => ({ ...prev, [fileKey]: blobUrl }));
          setLoadingFiles((prev) => ({ ...prev, [fileKey]: false }));
        };
        img.onerror = () => {
          setLoadingFiles((prev) => ({ ...prev, [fileKey]: false }));
        };
      } else {
        setFilePreviews((prev) => ({ ...prev, [fileKey]: blobUrl }));
        setLoadingFiles((prev) => ({ ...prev, [fileKey]: false }));
      }
    } catch (err) {
      setLoadingFiles((prev) => ({ ...prev, [fileKey]: false }));
    }
  };
  const handleDownload = async (fileKey: string) => {
    try {
      const blob = await vendorRepository.downloadVendorFile(fileKey);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const fileName = fileKey.split("/").pop() || "document";

      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showCenteredAlert("Download failed", "error");
    }
  };

  const showCenteredAlert = (message: string, status: "success" | "error" | 'warning') => {
    setCenterAlertMessage(message);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);
    setTimeout(() => setCenterAlertVisible(false), 1000);
  };

  // edit product modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleEditClick = (productId: string, vendorProductId: string) => {
    setSelectedProductId(productId);
    setSelectedVendorProductId(vendorProductId);
    setIsModalOpen(true);
  };

  //delete vendor product
  const doDelete = async () => {
    if (!deleteProductPayload) return;

    try {
      await vendorRepository.deleteVendorProduct(
        deleteProductPayload.vendorProductId,
        deleteProductPayload.productId
      );

      showCenteredAlert("Product deleted successfully", "success");
      setDeleteProductPayload(null);
      await loadVendorProducts();
    } catch (error) {
      showCenteredAlert("Failed to delete product", "error");
    }
  };

  const loadVendorProducts = async () => {
    if (!vendor?.vendorId) return;

    try {
      const data = await vendorRepository.getvendorProductbyVendor(
        vendor.vendorId
      );
      setVendorProducts({
        vendorProductId: data[0].vendorProductId,
        vendorId: data[0].vendorId,
        vendorName: data[0].vendorName,
        products: Array.isArray(data[0].products) ? data[0].products : [],
      });
    } catch (error) {
      setVendorProducts(null);
    }
  };

  useEffect(() => {

    loadVendorProducts();
  }, [vendor?.vendorId]);

  const confirmDeactivate = async () => {
    if (!deleteCandidate) return;
    try {
      await vendorRepository.deleteVendor(deleteCandidate);
      showCenteredAlert("Vendor deactivated successfully", "success");

      setTimeout(() => {
        navigate('/admin/vendors/VendorList');
      }, 2000);
    } catch (error) {
      showCenteredAlert("Failed to deactivate vendor", "error");
    } finally {
      setDeleteCandidate(null);
    }
  };

  if (!vendor) {
    return <Text>Loading vendor details...</Text>;
  }

  return (
    <>

      <Box bg={cardBg} borderRadius="2xl" boxShadow="lg" p={{ base: 4, md: 6 }}>
        <Flex justify="flex-end">
          <IconButton
            aria-label="Close"
            icon={<CloseIcon />}
            variant="ghost"
            size="sm"
            hidden={role === "Vendor"}
            onClick={() => navigate('/admin/vendors/VendorList', {state: location.state} )}
          />
        </Flex>
        <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={4}>
          <Text fontSize="2xl" fontWeight="700" color={textColor}>
            {vendor.name}
          </Text>
          <Flex gap={3}>
            {(role === "Admin" || role === "OrgAdmin" || role === "Vendor") && (
              <Button leftIcon={<MdEdit />} variant="darkBrand" size="sm" onClick={onOpen}>
                Edit Details
              </Button>
            )}
            <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside" isCentered >
              <ModalOverlay />
              <ModalContent>
                <ModalHeader bg={cardBg}>Edit Vendor</ModalHeader>
                <ModalCloseButton />
                <ModalBody bg={cardBg}>
                  <EditVendorForm
                    vendor={vendor}
                    onClose={onClose}
                    onUpdate={(updated) => {
                      setVendor(updated);
                      showCenteredAlert("Vendor updated successfully", "success");
                    }}
                    showCenteredAlert={showCenteredAlert}
                  />
                </ModalBody>
              </ModalContent>
            </Modal>

            {role === "Admin" && (
              <Button
                leftIcon={<MdDelete />}
                colorScheme="red"
                size="sm"
                onClick={() => setDeleteCandidate(vendor.vendorId)}
              >
                Deactivate
              </Button>
            )}
          </Flex>
        </Flex>

        <VStack spacing={8} align="stretch">
          <Box
            bg={infoBg}
            p={6}
            borderRadius="xl"
            border="1px solid"
            borderColor={borderColor}
          >
            <Flex align="center" gap={3} mb={6}>
              <Box
                w="45px"
                h="45px"
                borderRadius="full"
                overflow="hidden"
                bg="gray.100"
                border="1px solid"
                borderColor={borderColor}
              >
                <Avatar
                  src={vendor.logoUrl}
                 name={vendor.name}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
              </Box>

              <Text fontSize="xl" fontWeight="700">
                Vendor Information
              </Text>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>

              {/* LEFT SIDE : Vendor Details */}
              <VStack align="flex-start" spacing={3} fontSize="md" color={textColor}>
                <Text><strong>Name:</strong> {vendor.name}</Text>
                <Text><strong>Email:</strong> {vendor.contactEmail}</Text>
                <Text><strong>Contact Number:</strong> {vendor.contactNumber}</Text>
                <Text><strong>GSTIN:</strong> {vendor.gsTin}</Text>

                <Text>
                  <strong>Status:</strong>{" "}
                  <Badge
                    colorScheme={vendor.status === "Active" ? "green" : "red"}
                    borderRadius="full"
                    px={2}
                  >
                    {vendor.status}
                  </Badge>
                </Text>
              </VStack>
              {/* RIGHT SIDE : Address Details */}
              <SimpleGrid columns={2} spacing={6} fontSize="md" color={textColor}>
                <VStack align="flex-start" spacing={3}>
                  <Text><strong>Address Line:</strong> {vendor.address.locationName}</Text>
                  <Text><strong>Street:</strong> {vendor.address.street}</Text>
                  <Text><strong>City:</strong> {vendor.address.city}</Text>
                </VStack>

                <VStack align="flex-start" spacing={3}>
                  <Text><strong>State:</strong> {vendor.address.state}</Text>
                  <Text><strong>Country:</strong> {vendor.address.country}</Text>
                  <Text><strong>ZIP Code:</strong> {vendor.address.zipCode}</Text>
                </VStack>
              </SimpleGrid>

            </SimpleGrid>
          </Box>

          {allAttachments.length > 0 && (
            <Box
              bg={infoBg}
              p={6}
              borderRadius="xl"
              border="1px solid"
              borderColor={borderColor}
            >
              <Flex justify="space-between" align="center" mb={6}>
                <Text fontSize="xl" fontWeight="700">
                  Vendor Documents
                </Text>
                {(role === "Vendor") && (
                  <Button
                    leftIcon={<MdAdd />}
                    colorScheme="blue"
                    size="sm"
                    borderRadius="full"
                    onClick={onOpen}
                  >
                    Add Documents
                  </Button>
                )}
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {allAttachments.map((fileKey: string, index: number) => {
                  const fileName = fileKey.split("/").pop();
                  const lower = fileKey.toLowerCase();
                  const isImage =
                    lower.endsWith(".jpg") ||
                    lower.endsWith(".jpeg") ||
                    lower.endsWith(".png");

                  return (
                    <Box
                      key={index}
                      borderRadius="lg"
                      p={2}
                      textAlign="center"
                      bg="transparent"
                      transition="transform 0.2s"
                      _hover={{ transform: 'scale(1.02)' }}
                    >
                      {loadingFiles[fileKey] ? (
                        <Flex h="200px" align="center" justify="center">
                          <Spinner size="xl" />
                        </Flex>
                      ) : isImage && filePreviews[fileKey] ? (
                        <Image
                          src={filePreviews[fileKey]}
                          h="200px"
                          w="100%"
                          objectFit="contain"
                          borderRadius="md"
                          mb={3}
                          bg="whiteAlpha.50"
                        />
                      ) : (
                        <Flex
                          h="200px"
                          align="center"
                          justify="center"
                          bg="gray.100"
                          _dark={{ bg: 'whiteAlpha.50' }}
                          borderRadius="md"
                          mb={3}
                          fontSize="4xl"
                        >
                          📄
                        </Flex>
                      )}

                      <VStack spacing={3}>
                        <Text fontSize="sm" fontWeight="600" noOfLines={1} color={textColor}>
                          {fileName}
                        </Text>

                        <Button
                          leftIcon={<MdFileDownload />}
                          colorScheme="blue"
                          size="sm"
                          borderRadius="full"
                          px={8}
                          onClick={() => handleDownload(fileKey)}
                        >
                          Download
                        </Button>
                      </VStack>
                    </Box>
                  );
                })}
              </SimpleGrid>
            </Box>
          )}
          <Box bg={infoBg} p={6} borderRadius={'xl'} border="1px solid" borderColor={borderColor}>
            {(role === "Admin" || role === "Vendor" || role === "OrgAdmin") && (
              <Button
                colorScheme='blue'
                size="sm"
                borderRadius="full"
                float={"right"}
                onClick={() =>
                  navigate("/admin/vendors/VendorAddProduct", {
                    state: {
                      vendorId: vendor.vendorId,
                      vendorName: vendor.name,
                      source: "vendorDetail",
                    },
                  })
                }
              >
                <Icon as={MdAdd}
                  height="12px"
                  width="12px"
                />
                Add Products
              </Button>
            )}
            <Text fontSize={'xl'} fontWeight={'bold'} mb={4}>
              Vendor Products
            </Text>

            {selectedProductId && selectedVendorProductId && (
              <UpdateProductModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                productId={selectedProductId}
                vendorProductId={selectedVendorProductId}
                onProductUpdated={() => {
                  showCenteredAlert('Product updated successfully', 'success');
                  loadVendorProducts();
                  setIsModalOpen(false);
                }}
                showCenteredAlert={showCenteredAlert}
              />
            )}
            <Box>
              <Flex fontSize="md" color={textColor} overflow="scroll">
                <Table overflow={"scroll"}>
                  <Thead >
                    <Tr>
                      <Th>NAME</Th>
                      <Th>TYPE</Th>
                      <Th>VENDOR NAME</Th>
                      <Th>AMC</Th>
                      <Th>STATUS</Th>
                      {role === "Admin" && (<Th textAlign="center">ACTIONS</Th>)}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {vendorProducts?.products?.length ? (
                      vendorProducts.products.map((product) => (
                        <Tr key={product.productId}>
                          <Td>
                            <Flex align="center" gap={3}>
                              <Avatar
                                boxSize="40px"
                                size="sm"
                                name={product.productName}
                                src={product.productImage}
                              />
                              <Text>{product.productName}</Text>
                            </Flex>
                          </Td>
                          <Td>{product.productType}</Td>
                          <Td>{vendorProducts.vendorName}</Td>
                          <Td>{product.hasAMC ? "Yes" : "No"}</Td>
                          <Td>
                            <Badge
                              colorScheme={product.productStatus === "Active" ? "green" : "red"}
                            >
                              {product.productStatus}
                            </Badge>
                          </Td>
                          <Td>

                            {role === "Admin" && (
                              <Flex align="center" justify="center" gap={2}>
                                <Tooltip label="Edit Product" hasArrow>
                                  <IconButton
                                    aria-label="Edit"
                                    icon={<MdEdit size={20} />}
                                    size="sm"
                                    variant="ghost"
                                    color="blue.500"
                                    onClick={() =>
                                      handleEditClick(product.productId, vendorProducts.vendorProductId)
                                    }
                                  />
                                </Tooltip>

                                <Tooltip label="Delete Product" hasArrow>
                                  <IconButton
                                    aria-label="Delete"
                                    icon={<MdDelete size={20} />}
                                    variant="ghost"
                                    color="red.500"
                                    onClick={() =>
                                      setDeleteProductPayload({
                                        vendorProductId: vendorProducts.vendorProductId,
                                        productId: product.productId,
                                      })
                                    }
                                  />
                                </Tooltip>
                              </Flex>
                            )}

                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={7} textAlign="center">
                          No products found
                        </Td>
                      </Tr>
                    )}

                  </Tbody>
                </Table>
              </Flex>
            </Box>

          </Box>
        </VStack>
      </Box>

      {/* Confirm Deactivate Dialog */}
      <AlertDialog
        isOpen={!!deleteCandidate}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteCandidate(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deactivate Vendor
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to deactivate this vendor? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteCandidate(null)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeactivate} ml={3}>
                Deactivate
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      {/* Confirm Delete Product  */}
      <AlertDialog
        isOpen={!!deleteProductPayload}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteProductPayload(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deactivate Vendor
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this product? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteProductPayload(null)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={doDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="solid"
            bg={
              centerAlertMessage.toLowerCase().includes("delete") ||
                centerAlertMessage.toLowerCase().includes("deactivate")
                ? "red.500"
                : undefined
            }
            color={
              centerAlertMessage.toLowerCase().includes("delete") ||
                centerAlertMessage.toLowerCase().includes("deactivate")
                ? "white"
                : undefined
            }
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="xl"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon
              boxSize="40px"
              mr={0}
              color={
                centerAlertMessage.toLowerCase().includes("delete") ||
                  centerAlertMessage.toLowerCase().includes("deactivate")
                  ? "white"
                  : undefined
              }
            />
            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}

    </>
  );
}